import { defaultPerformers, validateGraph, type GraphIssue, type PerformedBy, type ProcessGraph, type RaciLetter, type Step, type StepType } from '@process-hub/shared';
import { useMemo, useState } from 'react';
import { Performer } from '../components/Badges';
import {
  addStep,
  blankStep,
  isDirty,
  minutesFromInput,
  moveStep,
  removeStep,
  removeTransition,
  setDefaultTransition,
  systemsToText,
  textToSystems,
  updateStep,
  upsertTransition,
} from '../editor/graph-edit';

/**
 * Draft step editor. Works on a copy of the graph; every change is validated with the shared
 * validateGraph so the owner sees blocking issues before saving. Save sends the whole graph —
 * the API replaces the draft's rows in one transaction, exactly as an interview proposal does.
 */

const STEP_TYPES: StepType[] = ['start', 'task', 'decision', 'wait', 'end'];
const PERFORMERS: PerformedBy[] = ['human', 'agent', 'hybrid'];
const LETTERS: RaciLetter[] = ['R', 'A', 'C', 'I'];

export function StepEditor({ initial, onSave, onCancel, saving, saveError }: { initial: ProcessGraph; onSave: (g: ProcessGraph) => void; onCancel: () => void; saving: boolean; saveError: string | null }) {
  const [graph, setGraph] = useState<ProcessGraph>(() => structuredClone(initial));
  const [selected, setSelected] = useState<string | null>(initial.steps[1]?.key ?? initial.steps[0]?.key ?? null);
  const issues = useMemo(() => validateGraph(graph), [graph]);
  const dirty = isDirty(initial, graph);
  const step = graph.steps.find((s) => s.key === selected) ?? null;
  const errors = issues.filter((i) => i.severity === 'error');

  const patch = (p: Partial<Omit<Step, 'key'>>) => step && setGraph((g) => updateStep(g, step.key, p));

  function add() {
    const role = step?.role ?? graph.steps[0]?.role ?? 'Role';
    const s = blankStep(graph, 'New step', role);
    setGraph((g) => addStep(g, s, step?.key ?? null));
    setSelected(s.key);
  }

  function remove() {
    if (!step) return;
    const idx = graph.steps.findIndex((s) => s.key === step.key);
    setGraph((g) => removeStep(g, step.key));
    setSelected(graph.steps[idx - 1]?.key ?? graph.steps[idx + 1]?.key ?? null);
  }

  return (
    <div className="editor">
      <div className="editor__toolbar">
        <div className="muted">{dirty ? 'Unsaved changes' : 'No changes'}{errors.length ? ` · ${errors.length} blocking issue${errors.length === 1 ? '' : 's'} (can still be saved as a draft)` : ''}</div>
        <div className="editor__actions">
          <button type="button" className="button button--secondary" onClick={onCancel} disabled={saving}>
            {dirty ? 'Discard changes' : 'Close editor'}
          </button>
          <button type="button" className="button" onClick={() => onSave(graph)} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
        </div>
      </div>
      {saveError && <div className="notice notice--error" style={{ margin: '0 18px 12px' }}>{saveError}</div>}
      <IssueList issues={issues} onPick={(k) => k && setSelected(k)} />

      <div className="editor__body">
        <ol className="editor__list" aria-label="Steps">
          {graph.steps.map((s, i) => (
            <li key={s.key} className={`editor__item${s.key === selected ? ' editor__item--selected' : ''}`}>
              <button type="button" className="editor__pick" onClick={() => setSelected(s.key)} aria-current={s.key === selected} aria-label={`Edit ${s.name}`}>
                <span className="mono muted">{i + 1}</span>
                <span className="editor__pick-name">
                  {s.name}
                  {s.stepType !== 'task' && <span className="tag">{s.stepType.toUpperCase()}</span>}
                </span>
                <Performer value={s.performedBy} />
              </button>
              <span className="editor__reorder">
                <button type="button" aria-label={`Move ${s.name} up`} onClick={() => setGraph((g) => moveStep(g, s.key, -1))} disabled={i === 0}>
                  ↑
                </button>
                <button type="button" aria-label={`Move ${s.name} down`} onClick={() => setGraph((g) => moveStep(g, s.key, 1))} disabled={i === graph.steps.length - 1}>
                  ↓
                </button>
              </span>
            </li>
          ))}
          <li className="editor__item editor__item--add">
            <button type="button" className="link-button" onClick={add}>
              + Add a step {step ? `after "${step.name}"` : ''}
            </button>
          </li>
        </ol>

        {step ? (
          <div className="editor__form">
            <label className="field">
              <span>Name</span>
              <input value={step.name} onChange={(e) => patch({ name: e.target.value })} maxLength={200} />
            </label>
            <div className="editor__row">
              <label className="field">
                <span>Type</span>
                <select value={step.stepType} onChange={(e) => patch({ stepType: e.target.value as StepType })}>
                  {STEP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Role (job title)</span>
                <input value={step.role} onChange={(e) => patch({ role: e.target.value })} maxLength={120} />
              </label>
              <label className="field">
                <span>Performed by</span>
                <select value={step.performedBy} onChange={(e) => patch({ performedBy: e.target.value as PerformedBy })}>
                  {PERFORMERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="field">
              <span>Description</span>
              <textarea value={step.description ?? ''} onChange={(e) => patch({ description: e.target.value || null })} rows={2} maxLength={2000} />
            </label>
            <div className="editor__row">
              <label className="field">
                <span>Systems (name, or name (reads|writes))</span>
                <input value={systemsToText(step.systems)} onChange={(e) => patch({ systems: textToSystems(e.target.value) })} placeholder="HubSpot (writes), NetSuite" />
              </label>
              <label className="field">
                <span>Touch (min)</span>
                <input inputMode="numeric" value={step.touchMinutes ?? ''} onChange={(e) => patch({ touchMinutes: minutesFromInput(e.target.value) })} />
              </label>
              <label className="field">
                <span>Wait (min)</span>
                <input inputMode="numeric" value={step.waitMinutes ?? ''} onChange={(e) => patch({ waitMinutes: minutesFromInput(e.target.value) })} />
              </label>
            </div>
            <fieldset className="editor__flags">
              <legend>What this step needs</legend>
              <label>
                <input type="checkbox" checked={step.requiresJudgment} onChange={(e) => patch({ requiresJudgment: e.target.checked })} /> Judgment
              </label>
              <label>
                <input type="checkbox" checked={step.involvesNegotiation} onChange={(e) => patch({ involvesNegotiation: e.target.checked })} /> Negotiation
              </label>
              <label>
                <input type="checkbox" checked={step.affectsClientTrust} onChange={(e) => patch({ affectsClientTrust: e.target.checked })} /> Client trust
              </label>
            </fieldset>
            {!defaultPerformers(step).includes(step.performedBy) && (
              <label className="field field--override">
                <span>This performer is outside the default for these flags. Why? (required; the version will go through the Compliance gate)</span>
                <input value={step.overrideReason ?? ''} onChange={(e) => patch({ overrideReason: e.target.value || null })} maxLength={1000} />
              </label>
            )}
            <label className="field">
              <span>Why this stays with a person (owner's words)</span>
              <input value={step.whyNotAutomate ?? ''} onChange={(e) => patch({ whyNotAutomate: e.target.value || null })} maxLength={1000} />
            </label>
            <label className="field">
              <span>Notes</span>
              <textarea value={step.notes ?? ''} onChange={(e) => patch({ notes: e.target.value || null })} rows={2} maxLength={2000} />
            </label>

            <RaciEditor step={step} onChange={(raci) => patch({ raci })} />
            <TransitionEditor graph={graph} step={step} setGraph={setGraph} />

            <div className="editor__danger">
              <button type="button" className="link-button link-button--danger" onClick={remove}>
                Remove this step
              </button>
            </div>
          </div>
        ) : (
          <div className="notice" style={{ margin: 18 }}>
            Select a step to edit it.
          </div>
        )}
      </div>
    </div>
  );
}

function IssueList({ issues, onPick }: { issues: GraphIssue[]; onPick: (key: string | null) => void }) {
  if (issues.length === 0) return null;
  return (
    <div>
      {issues.map((i, n) => (
        <button key={n} type="button" className={`issue issue--button${i.severity === 'error' ? ' issue--error' : ''}`} onClick={() => onPick(i.stepKey)}>
          {i.message}
        </button>
      ))}
    </div>
  );
}

function RaciEditor({ step, onChange }: { step: Step; onChange: (raci: Step['raci']) => void }) {
  const [role, setRole] = useState('');
  const [letter, setLetter] = useState<RaciLetter>('C');
  return (
    <fieldset className="editor__raci">
      <legend>RACI</legend>
      <ul>
        {step.raci.map((r, i) => (
          <li key={`${r.role}-${r.letter}-${i}`}>
            <span className="mono">{r.letter}</span> {r.role}
            <button type="button" className="link-button" aria-label={`Remove ${r.letter} ${r.role}`} onClick={() => onChange(step.raci.filter((_, j) => j !== i))}>
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="editor__row">
        <select value={letter} onChange={(e) => setLetter(e.target.value as RaciLetter)} aria-label="RACI letter">
          {LETTERS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Job title" aria-label="RACI role" />
        <button
          type="button"
          className="button button--secondary"
          disabled={role.trim().length < 2}
          onClick={() => {
            onChange([...step.raci, { role: role.trim(), letter }]);
            setRole('');
          }}
        >
          Add
        </button>
      </div>
    </fieldset>
  );
}

function TransitionEditor({ graph, step, setGraph }: { graph: ProcessGraph; step: Step; setGraph: (f: (g: ProcessGraph) => ProcessGraph) => void }) {
  const outs = graph.transitions.map((t, index) => ({ t, index })).filter(({ t }) => t.from === step.key);
  const others = graph.steps.filter((s) => s.key !== step.key);
  return (
    <fieldset className="editor__transitions">
      <legend>Next steps {step.stepType === 'decision' ? '— label each outcome; one is the default' : ''}</legend>
      {outs.length === 0 && step.stepType !== 'end' && <div className="muted">No next step yet.</div>}
      {outs.map(({ t, index }) => (
        <div key={index} className="editor__row editor__transition">
          <select value={t.to} aria-label="Next step" onChange={(e) => setGraph((g) => upsertTransition(g, index, { ...t, to: e.target.value }))}>
            {others.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
          <input value={t.condition ?? ''} placeholder={step.stepType === 'decision' ? 'Outcome label' : 'Label (optional)'} aria-label="Condition" onChange={(e) => setGraph((g) => upsertTransition(g, index, { ...t, condition: e.target.value || null }))} />
          <label className="editor__default">
            <input type="radio" name={`default-${step.key}`} checked={t.isDefault} onChange={() => setGraph((g) => setDefaultTransition(g, step.key, index))} /> default
          </label>
          <button type="button" className="link-button" aria-label="Remove transition" onClick={() => setGraph((g) => removeTransition(g, index))}>
            remove
          </button>
        </div>
      ))}
      {step.stepType !== 'end' && others.length > 0 && (
        <button
          type="button"
          className="link-button"
          onClick={() => setGraph((g) => upsertTransition(g, null, { from: step.key, to: others[0].key, condition: step.stepType === 'decision' ? 'Outcome' : null, isDefault: outs.length === 0 && step.stepType === 'decision' }))}
        >
          + Add a next step
        </button>
      )}
    </fieldset>
  );
}
